import PropTypes from 'prop-types';
import React, { useState } from 'react';
import classNames from 'classnames/bind';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import Button from 'yoda-core-components/lib/components/Button';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import BirthdayIcon from '../../assets/images/birthdayCP.svg';
import MoneyBagIcon from '../../assets/images/moneybagCP.svg';
import ChallengeIcon from '../../assets/images/lockCP.svg';
// eslint-disable-next-line import/no-named-as-default
import CashpassExclusionsSlidePanel from './CashpassExclusionsSlidePanel';
import * as styles from './CashpassCard.css';

const cx = classNames.bind(styles);

/**
 * Standalone "Exclusions & Details" page used as the fallback when the
 * slide-panel feature flag is disabled.
 */
export const EXCLUSIONS_URL =
    'https://www.jcpenney.com/m/rewards-redemptions-exclusions?pageId=pg40067900006';

/**
 * Maps each cashpassType to the icon component rendered inside the card.
 * Falls back to the reward (money bag) icon for any unknown type.
 */
const CASHPASS_ICONS = {
    birthday: {
        Component: BirthdayIcon,
        width: '72px',
        height: '78px',
        viewBox: '0 0 72 78',
        automationId: 'cashpass-icon-birthday',
    },
    JCPRWD: {
        Component: MoneyBagIcon,
        width: '78px',
        height: '78px',
        viewBox: '0 0 78 78',
        automationId: 'cashpass-icon-reward',
    },
    challenge: {
        Component: ChallengeIcon,
        width: '54px',
        height: '78px',
        viewBox: '0 0 54 78',
        automationId: 'cashpass-icon-challenge',
    },
};

const getIconConfig = (cashpassType) => CASHPASS_ICONS[cashpassType] || CASHPASS_ICONS.JCPRWD;

const CashpassCard = (props) => {
    const {
        cashpassType,
        title,
        eligible,
        expire,
        isAppliedToCart,
        onApplyToCart,
        onRemoveFromCart,
        automationId,
        enableExclusionsSlidePanel,
    } = props;

    const [isExclusionsOpen, setIsExclusionsOpen] = useState(false);

    const {
        Component: CashpassIcon,
        width: iconWidth,
        height: iconHeight,
        viewBox,
        automationId: iconAutomationId,
    } = getIconConfig(cashpassType);

    const openExclusions = (event) => {
        if (event && event.preventDefault) {
            event.preventDefault();
        }
        setIsExclusionsOpen(true);
    };

    const closeExclusions = () => setIsExclusionsOpen(false);

    const handleCartClick = (event) => {
        if (isAppliedToCart) {
            onRemoveFromCart(event, cashpassType);
        } else {
            onApplyToCart(event, cashpassType);
        }
    };

    const buttonLabel = isAppliedToCart ? 'Remove from Cart' : 'Apply to Cart';
    const buttonAutomationId = isAppliedToCart
        ? 'cashpass-remove-from-cart'
        : 'cashpass-apply-to-cart';
    const buttonWidthClass = isAppliedToCart ? 'max-w-[189px]' : 'max-w-[149px]';

    return (
        <div
            className={`${cx('cashpassCard')} ${dt([
                'bg-white',
                'border',
                'border-solid',
                'border-gray-25',
                'rounded-2xl',
                'flex',
                'flex-col',
                'gap-4',
                'items-center',
                'justify-center',
                'px-6',
                'py-4',
                'w-[280px]',
                'h-[376px]',
            ])}`}
            data-automation-id={automationId}
        >
            <div className={dt(['flex', 'items-center', 'justify-center', 'h-[78px]'])}>
                <Icon
                    iconType="svg"
                    width={iconWidth}
                    height={iconHeight}
                    viewBox={viewBox}
                    automationId={iconAutomationId}
                >
                    <CashpassIcon />
                </Icon>
            </div>

            <div
                className={dt([
                    'font-bold',
                    'text-xlarge',
                    'text-black',
                    'text-center',
                    'w-full',
                    'break-words',
                ])}
                data-automation-id="cashpass-title"
            >
                {title}
            </div>

            <div
                className={dt([
                    'font-normal',
                    'text-small',
                    'text-black',
                    'text-center',
                    'w-full',
                    'break-words',
                ])}
                data-automation-id="cashpass-eligible"
            >
                {eligible}
            </div>

            {expire && (
                <div
                    className={dt([
                        'font-normal',
                        'text-small',
                        'text-gray-70',
                        'text-center',
                        'w-full',
                        'break-words',
                    ])}
                    data-automation-id="cashpass-expire"
                >
                    {`Exp. ${expire}`}
                </div>
            )}

            <div className={dt(['flex', 'flex-col', 'items-center', 'justify-center', 'w-full'])}>
                {enableExclusionsSlidePanel ? (
                    <Button
                        buttonType="Link"
                        size="Sm"
                        onClick={openExclusions}
                        automationId="cashpass-exclusions-link"
                        className={dt([
                            'text-black',
                            'text-center',
                            'font-medium',
                            'underline-offset-4',
                        ])}
                    >
                        Exclusions &amp; Details
                    </Button>
                ) : (
                    <Button
                        buttonType="Link"
                        size="Sm"
                        linkUrl={EXCLUSIONS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        automationId="cashpass-exclusions-link"
                        className={dt([
                            'text-black',
                            'text-center',
                            'font-medium',
                            'underline-offset-4',
                        ])}
                    >
                        Exclusions &amp; Details
                    </Button>
                )}
            </div>

            <div className={dt(['flex', 'flex-col', 'items-center', 'w-full', 'mt-3'])}>
                <Button
                    buttonType="Secondary"
                    size="Default"
                    onClick={handleCartClick}
                    automationId={buttonAutomationId}
                    className={dt(['w-full', buttonWidthClass])}
                >
                    {buttonLabel}
                </Button>
            </div>

            {enableExclusionsSlidePanel && (
                <CashpassExclusionsSlidePanel isOpen={isExclusionsOpen} onClose={closeExclusions} />
            )}
        </div>
    );
};

CashpassCard.propTypes = {
    /**
     * Certificate type that determines which icon is displayed (e.g. 'birthday',
     * 'JCPRWD', 'challenge'). Unknown types fall back to the reward icon.
     */
    cashpassType: PropTypes.string.isRequired,
    /** Card heading, e.g. "$10 Birthday Cashpass". */
    title: PropTypes.string.isRequired,
    /** Eligibility copy shown below the title. */
    eligible: PropTypes.string,
    /** Expiration text set by the caller, rendered as "Exp. {expire}". */
    expire: PropTypes.string,
    /** When true the button reads "Remove from Cart", otherwise "Apply to Cart". */
    isAppliedToCart: PropTypes.bool,
    /** Handler invoked when the card is not yet applied and the button is clicked. */
    onApplyToCart: PropTypes.func,
    /** Handler invoked when the card is applied and the button is clicked. */
    onRemoveFromCart: PropTypes.func,
    /** Automation id for the card container. */
    automationId: PropTypes.string,
    /**
     * Feature flag. When true (default), "Exclusions & Details" opens in a slide
     * panel. When false, it falls back to opening the standalone page in a new tab.
     */
    enableExclusionsSlidePanel: PropTypes.bool,
};

CashpassCard.defaultProps = {
    eligible: 'Available to use in store or online.',
    expire: '',
    isAppliedToCart: false,
    onApplyToCart: () => {},
    onRemoveFromCart: () => {},
    automationId: 'cashpass-card',
    enableExclusionsSlidePanel: true,
};

export default CashpassCard;
